/**
 * Path: packages/frontend/src/core/services/GeminiService.ts
 * Task: Handles secure, robust communication with the Google Gemini API.
 * Includes automatic exponential backoff for rate limits and deep error parsing.
 */

export interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
}

export class GeminiService {
  /**
   * Sends a conversational query to Gemini, providing the project markdown as context.
   */
  static async sendChatMessage(userQuery: string, chatHistory: ChatMessage[], systemContext: string): Promise<string> {
    // INFO: Trage hier deinen echten Google Gemini API Key ein!
    // Du bekommst ihn kostenlos unter: https://aistudio.google.com/app/apikey
    const apiKey = "";

    if (!apiKey || apiKey.trim() === "") {
      throw new Error("Missing API Key: Please add your Gemini API Key in GeminiService.ts");
    }

    const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`;

    // Map internal history format to Gemini API expected format
    const contents = chatHistory.map(msg => ({
      role: msg.role === 'assistant' ? 'model' : 'user',
      parts: [{ text: msg.text }]
    }));

    // Append the new user query
    contents.push({ role: "user", parts: [{ text: userQuery }] });

    const payload = {
      contents: contents,
      systemInstruction: {
        parts: [{ text: systemContext }]
      }
    };

    // Exponential Backoff for resilient API calls
    const maxRetries = 3;
    let delay = 1000;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (response.status === 429 && attempt < maxRetries) {
          // Rate limited, wait and retry
          await new Promise(resolve => setTimeout(resolve, delay));
          delay *= 2;
          continue;
        }

        if (!response.ok) {
          // Robustes Parsing der echten Google-Fehlermeldung
          let errorDetail = response.statusText;
          try {
            const errorJson = await response.json();
            if (errorJson.error && errorJson.error.message) {
              errorDetail = errorJson.error.message;
            }
          } catch (e) {
            // Fallback, falls die Antwort kein JSON ist
          }
          throw new Error(`${errorDetail} (HTTP ${response.status})`);
        }

        const result = await response.json();
        const candidate = result.candidates?.[0];

        if (candidate && candidate.content?.parts?.[0]?.text) {
          return candidate.content.parts[0].text;
        } else {
          throw new Error("Unexpected response structure from Gemini API.");
        }
      } catch (error: any) {
        if (attempt === maxRetries || error.message.includes("Missing API Key") || error.message.includes("API key not valid")) {
          // Bei finalen Fehlern oder fehlendem Key brechen wir sofort ab
          throw error;
        }
      }
    }

    throw new Error("Failed to reach AI service after multiple attempts.");
  }
}
